/**
 * Lazy, isolated wrapper around the (heavy) mermaid library. Dynamically
 * imported so mermaid (~600KB) only enters the bundle when a diagram actually
 * renders. Initializes mermaid once, then renders code → SVG, capturing parse
 * errors as a result rather than throwing (invalid diagrams must degrade to a
 * shown error, never a blank).
 */

import { buildMermaidTheme, type TokenReader } from './mermaid-theme';

export type MermaidResult =
  | { ok: true; svg: string }
  | { ok: false; error: string };

/** Read a CSS custom property's resolved value off the document root. */
const readCssToken: TokenReader = (token) => {
  if (typeof document === 'undefined') return '';
  return getComputedStyle(document.documentElement).getPropertyValue(token);
};

async function getMermaid() {
  const mod = await import('mermaid');
  const mermaid = mod.default;
  // Re-initialize on every render so the theme picks up the CURRENT design
  // tokens — this is how the diagram tracks light/dark mode (mermaid bakes
  // colors into the SVG at render time and can't read CSS vars itself).
  mermaid.initialize({
    startOnLoad: false,
    securityLevel: 'strict',
    theme: 'base',
    themeVariables: buildMermaidTheme(readCssToken),
    // Emit intrinsic pixel width/height instead of width="100%". With a real
    // intrinsic size, `max-width: 100%` caps wide diagrams responsively and the
    // container can shrink-wrap narrow ones — so pan/zoom and the centered box
    // size to the actual diagram rather than stretching/squishing it.
    flowchart: { useMaxWidth: false },
    sequence: { useMaxWidth: false },
    gantt: { useMaxWidth: false },
    er: { useMaxWidth: false },
    journey: { useMaxWidth: false },
    pie: { useMaxWidth: false },
  });
  return mermaid;
}

/**
 * Render mermaid `code` to an SVG string. `id` must be a DOM-id-safe, unique
 * string (mermaid uses it to scope the rendered SVG). Returns an error result
 * for empty input or any parse/render failure.
 */
export async function renderMermaid(id: string, code: string): Promise<MermaidResult> {
  if (!code.trim()) {
    return { ok: false, error: 'Empty diagram' };
  }
  try {
    const mermaid = await getMermaid();
    const { svg } = await mermaid.render(id, code);
    return { ok: true, svg };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : 'Failed to render diagram' };
  }
}
